import { useState, useContext, useId } from "react";
import classNames from "classnames";
import { Button, Flex, Image, Text, Icon } from "@simplybusiness/mobius";
import { lock } from "@simplybusiness/icons";
import { CardPopover } from "../../../QuoteCards/QuoteCard/InfoModal/CardPopover";
import { PaymentTermsChecklist } from "../../../shared/PersistentSummary";
import { CombinedPaymentContext } from "../../../shared/PersistentSummary/CombinedPaymentContext";
import type { CallToActionProps, PaymentTermsOption } from "./types";

export const CallToAction = ({
  enableCheckoutFormSubmission,
  isFormSubmitting,
  fireCheckoutButtonClickedEvent,
  onCallToActionClick,
  buttonText,
  poweredByStripeLogo,
  agreementChecklist,
  errorMessage = "Please accept all terms to continue",
  eventTrackerCallback,
  legalAgreementButtonsNotCheckedCallback,
  checkboxLinkClickedCallback,
}: CallToActionProps) => {
  const [checkedAgreements, setCheckedAgreements] = useState<string[]>([]);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const isCombinedPaymentPage = useContext(CombinedPaymentContext);
  const checklistId = useId();

  const allAccepted = agreementChecklist.every((opt: PaymentTermsOption) =>
    checkedAgreements.includes(opt.value),
  );
  const showError = hasAttemptedSubmit && !allAccepted;

  const validateAgreements = () => {
    setHasAttemptedSubmit(true);
    if (!allAccepted) {
      legalAgreementButtonsNotCheckedCallback();
      return false;
    }
    return true;
  };

  const handleCheckoutClick = (e: React.MouseEvent) => {
    if (!validateAgreements()) {
      e.preventDefault();
      return;
    }
    fireCheckoutButtonClickedEvent();
  };

  const handlePayClick = () => {
    if (validateAgreements()) {
      onCallToActionClick(true);
    }
  };

  const hasAgreements = agreementChecklist.length > 0;

  return (
    <div
      className={classNames("mobius mobius-call-to-action", {
        "mobius-call-to-action--sticky": !enableCheckoutFormSubmission,
      })}
    >
      {hasAgreements && (
        <PaymentTermsChecklist
          id={checklistId}
          options={agreementChecklist}
          value={checkedAgreements}
          onChange={setCheckedAgreements}
          isInvalid={showError}
          errorMessage={showError ? errorMessage : undefined}
          checkboxLinkClickEvent={checkboxLinkClickedCallback}
        />
      )}
      <Flex
        data-testid="payment-cta-container"
        className="mobius-call-to-action__cta-container"
      >
        {isCombinedPaymentPage && (
          <div className="mobius-call-to-action__popover-wrapper">
            <CardPopover
              toggleText="Cancel anytime"
              body="You can easily cancel your insurance policy at any time during the policy period. Please note that penalties may apply."
              onOpen={eventTrackerCallback}
            />
          </div>
        )}
        {enableCheckoutFormSubmission ? (
          <Button
            className="mobius-call-to-action__checkout-button"
            type="submit"
            size="md"
            isLoading={isFormSubmitting}
            isDisabled={isFormSubmitting}
            data-testid="submit-btn"
            onClick={handleCheckoutClick}
          >
            Checkout
          </Button>
        ) : (
          <Flex flexDirection="column">
            <Button
              className="mobius-call-to-action__checkout-button mobius-call-to-action--sticky"
              size="md"
              data-testid="open-payment-drawer-btn"
              onClick={handlePayClick}
            >
              {buttonText}
            </Button>
            <Flex
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
            >
              <Icon
                icon={lock}
                className="mobius-call-to-action__secure-checkout-icon"
              />
              <Text className="mobius-call-to-action__secure-checkout-text">
                Secure Checkout
              </Text>
              <Image
                className="mobius-call-to-action__stripe-logo"
                src={poweredByStripeLogo}
                alt="Powered by Stripe"
              />
            </Flex>
          </Flex>
        )}
      </Flex>
    </div>
  );
};
